import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { partyAPI } from '../services/api';
import toast from 'react-hot-toast';

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const fail = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  toast.error(typeof detail === 'string' ? detail : fallback);
};

const PartyLanCard = ({ isFamily }) => {
  const queryClient = useQueryClient();
  const [startsAt, setStartsAt] = useState('');
  const [emails, setEmails] = useState(['', '', '']);
  const { data, isLoading } = useQuery('partyLanMine', partyAPI.mine, {
    refetchInterval: 15000,
    enabled: !isFamily,
  });

  const invalidate = () => {
    queryClient.invalidateQueries('partyLanMine');
    queryClient.invalidateQueries('userStats');
    queryClient.invalidateQueries('cashback');
  };

  const create = useMutation(
    () =>
      partyAPI.create({
        starts_at: new Date(startsAt).toISOString(),
        emails: emails.map((e) => e.trim()).filter(Boolean),
      }),
    {
      onSuccess: () => {
        toast.success('Party LAN created. Your wallet hold is locked.');
        invalidate();
      },
      onError: (e) => fail(e, 'Could not create Party LAN.'),
    }
  );

  const upcoming = data?.upcoming;
  const last = data?.last;
  const invites = data?.invites || [];
  const bump = Number(data?.access_bump_eur || 0);

  if (isFamily) {
    return (
      <div className="gaming-card rounded-lg p-6">
        <h3 className="text-xl font-semibold text-white mb-2">Party LAN</h3>
        <p className="text-sm text-white/70">
          Households use Family (one GPU, four seats, no no-show bill). Party LAN is for
          friends who each have their own login.
        </p>
      </div>
    );
  }

  return (
    <div className="gaming-card rounded-lg p-6 space-y-4">
      <div>
        <h3 className="text-xl font-semibold text-white mb-1">Party LAN</h3>
        <p className="text-sm text-white/70">
          Named RSVP. Yes takes a wallet hold of about {data?.hold_mkd_example || 15} MKD
          (1 hour at 1/4 of a 40 MKD/h box + {data?.reliability_fee_mkd || 5} MKD fee).
          People who show pay their quarter while they play. No-shows pay the reserved
          seat + fee. Dummy accounts cannot farm this — the hold is real money.
        </p>
      </div>

      {bump > 0 ? (
        <p className="text-sm text-amber-200">
          You missed a Party LAN. Next access invoice includes +€{bump.toFixed(0)}. It
          drops after you show up to a night.
        </p>
      ) : null}

      {isLoading ? <p className="text-white/50 text-sm">Loading parties…</p> : null}

      {upcoming ? (
        <div className="space-y-2 text-sm text-white/80">
          <p className="text-white font-medium">Upcoming · {formatWhen(upcoming.starts_at)}</p>
          <p>
            Status {upcoming.status} · RSVP Yes {upcoming.yes_count}/4 · checked in{' '}
            {upcoming.show_count}
          </p>
          <p>
            Hold {Number(upcoming.hold_mkd).toFixed(2)} MKD · your RSVP {upcoming.your_rsvp || '—'}
          </p>
          <ul className="text-xs text-white/60 space-y-0.5">
            {(upcoming.members || []).map((m) => (
              <li key={m.email}>
                {m.is_host ? 'Host' : 'Seat'}: {m.email} · {m.rsvp}
                {m.showed ? ' · here' : ''}
                {m.you ? ' (you)' : ''}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-2 pt-1">
            {upcoming.your_rsvp === 'invited' ? (
              <>
                <ActionButton
                  label="RSVP Yes (hold wallet)"
                  onClick={() => partyAPI.rsvpYes(upcoming.id).then(invalidate)}
                />
                <ActionButton
                  label="RSVP No"
                  onClick={() => partyAPI.rsvpNo(upcoming.id).then(invalidate)}
                />
              </>
            ) : null}
            {upcoming.your_rsvp === 'yes' && !upcoming.your_showed ? (
              <ActionButton
                label="I'm here — check in"
                onClick={() => partyAPI.checkIn(upcoming.id).then(invalidate)}
              />
            ) : null}
            {upcoming.you_are_host ? (
              <ActionButton
                label="Cancel night (release holds)"
                onClick={() => partyAPI.cancel(upcoming.id).then(invalidate)}
              />
            ) : null}
          </div>
        </div>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <label className="block text-sm text-white">
            LAN night start (your local time)
            <input
              type="datetime-local"
              required
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-md text-black"
            />
          </label>
          {emails.map((value, idx) => (
            <label key={idx} className="block text-sm text-white">
              Friend {idx + 1} email
              <input
                type="email"
                required
                value={value}
                onChange={(e) => {
                  const next = [...emails];
                  next[idx] = e.target.value;
                  setEmails(next);
                }}
                className="mt-1 w-full px-3 py-2 rounded-md text-black"
              />
            </label>
          ))}
          <button
            type="submit"
            disabled={create.isLoading}
            className="w-full bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
          >
            {create.isLoading ? 'Holding wallet…' : 'Create Party LAN (locks your hold)'}
          </button>
        </form>
      )}

      {invites.length > 0 && !upcoming ? (
        <div className="space-y-2">
          <p className="text-white text-sm font-medium">You're invited</p>
          {invites.map((p) => (
            <div key={p.id} className="text-sm text-white/80 space-y-1">
              <p>{formatWhen(p.starts_at)} · hold {Number(p.hold_mkd).toFixed(2)} MKD</p>
              <div className="flex gap-2">
                <ActionButton
                  label="RSVP Yes"
                  onClick={() => partyAPI.rsvpYes(p.id).then(invalidate)}
                />
                <ActionButton
                  label="RSVP No"
                  onClick={() => partyAPI.rsvpNo(p.id).then(invalidate)}
                />
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {last && last.your_outcome ? (
        <p className="text-xs text-white/55">
          Last night: {last.your_outcome === 'no_show'
            ? `you missed it — ${Number(last.your_captured_mkd || 0).toFixed(2)} MKD captured from hold`
            : last.your_outcome === 'show'
              ? 'you showed. Subscription unchanged.'
              : last.outcome || last.your_outcome}
          {last.start_quad && last.your_outcome === 'show'
            ? ' Start the 4-seat GPU from Game.'
            : ''}
          {last.outcome === 'solo_offer' && last.your_outcome === 'show'
            ? ' Only you showed — play Solo 1:1 from Game, not the 4-seat box.'
            : ''}
        </p>
      ) : null}
    </div>
  );
};

const ActionButton = ({ label, onClick }) => {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      disabled={busy}
      className="text-xs px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-white disabled:opacity-50"
      onClick={async () => {
        setBusy(true);
        try {
          await onClick();
        } catch (e) {
          fail(e, label);
        } finally {
          setBusy(false);
        }
      }}
    >
      {busy ? '…' : label}
    </button>
  );
};

export default PartyLanCard;
